<template>
  <BaseEditModal
    :displayName="assetMaster.name"
    helpFileName="AssetDialog"
    :itemtype="itemtype"
    :tabs="tabs"
    :icon="'mdi-cube-scan'"
    :masterObj="assetMaster"
    :saveFunc="saveAsset"
    :deleteFunc="deleteAsset"
    :instanceDeletable="!assetMaster.default_template"
    @close="emit('close')"
    @locked="(bool) => emit('locked', bool)">
    <template v-slot:prepend-buttons>
      <v-btn
        v-if="isDownloadable"
        variant="flat"
        style="background-color: var(--inner-background)"
        class="mr-2"
        :href="
          state.devRedirect +
          '/api/download_asset?uid=' +
          selected.uid +
          '&token=' +
          auth()
        ">
        <v-icon style="font-size: 22px">mdi-tray-arrow-down</v-icon>
      </v-btn>
    </template>
  </BaseEditModal>
</template>
<script setup>
import { deluge, state } from '../../stores/deluge';
import BaseEditModal from '../Shared/Modals/BaseEditModal.vue';
import { $http } from '../../stores/http';
import { computed, ref, onMounted } from 'vue';

// Asset Sub Components
import AssetDetails from './AssetModalComponents/AssetDetails.vue';
import AssetTags from './AssetModalComponents/AssetTags.vue';
import AssetExpire from './AssetModalComponents/AssetExpire.vue';
import AssetInfo from './AssetModalComponents/AssetInfo.vue';
import Formats from './AssetModalComponents/Formats.vue';
import AssetConditions from './AssetModalComponents/AssetConditions.vue';
import OptimizedAssets from './AssetModalComponents/OptimizedAssets.vue';
import AssetUsage from './AssetModalComponents/AssetUsage.vue';

function auth() {
  return localStorage.getItem('authentication_token');
}

const props = defineProps(['selected', 'itemtype']);
const emit = defineEmits(['close', 'locked', 'deleted']);

const assetMaster = ref(JSON.parse(JSON.stringify(props.selected)));

// Refetch asset data on mount to get latest extra_info (e.g., conversion variants)
onMounted(async () => {
  try {
    const response_raw = await $http(
      'GET',
      `/get_assets?filter_uids=${props.selected.uid}`
    );
    const response = JSON.parse(response_raw);

    // Update with fresh data if available
    if (response && response[props.selected.uid]) {
      assetMaster.value = {
        ...assetMaster.value,
        ...response[props.selected.uid],
      };
    }
  } catch (error) {
    console.error('Failed to refetch asset data:', error);
    // Continue with cached data
  }
});

// Determine if asset is downloadable based on asset type
const isDownloadable = computed(() => {
  // If there's no asset_type or value, it's not downloadable
  if (
    (!props.selected?.asset_type || !props.selected?.value) &&
    !props.selected.asset_type.includes('json')
  ) {
    return false;
  }

  // These asset types are not downloadable
  const nonDownloadableTypes = ['ndi', 'video_feed', 'live_stream', 'text'];
  if (
    nonDownloadableTypes.some((type) =>
      props.selected.asset_type.includes(type)
    )
  ) {
    return false;
  }

  return true;
});

// Tabs - must be 0 indexed.
const tabs = [
  {
    id: 0,
    title: 'Asset Details',
    icon: 'mdi-rename-box',
    display: true,
    component: AssetDetails,
  },
  {
    id: 1,
    title: 'Asset Tags',
    icon: 'mdi-tag',
    display: true,
    component: AssetTags,
  },
  {
    id: 2,
    title: 'Asset Expire',
    icon: 'mdi-broom',
    display: true,
    component: AssetExpire,
  },
  {
    id: 3,
    title: 'Asset Info',
    icon: 'mdi-information',
    display: true,
    component: AssetInfo,
  },
  {
    id: 4,
    title: 'Formats',
    icon: 'mdi-aspect-ratio',
    display: true, // Always show the tab
    component: Formats,
  },
  {
    id: 5,
    title: 'Conditions',
    icon: 'mdi-motion-play-outline',
    display: true, // Always show - conditions determine asset selection in groups
    component: AssetConditions,
  },
  {
    id: 6,
    title: 'Optimizations',
    icon: 'mdi-lightning-bolt',
    display: computed(() => {
      // Only show if optimizations exist
      return assetMaster.value?.extra_info?.optimizations?.length > 0;
    }),
    component: OptimizedAssets,
  },
  {
    id: 7,
    title: 'Usage',
    icon: 'mdi-map-marker-path',
    display: true,
    component: AssetUsage,
  },
];

async function saveAsset(templateUID, newTemplateData) {
  return await $http(
    'PUT',
    '/update_asset?asset_uid=' + templateUID,
    JSON.stringify(newTemplateData)
  );
}
async function deleteAsset(templateUID) {
  return await $http('DELETE', '/delete_asset?asset_uid=' + templateUID);
}
// END Tabs
</script>

<style scoped></style>
